import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { Typography, DatePicker, Select, Input, Tooltip, Button, message, Popconfirm, Tag, Space } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { Dayjs } from 'dayjs';

import { useAppSelector, useAppDispatch } from '../../Modules/Presentation/hooks';
import {
  selectLogs,
  selectLogLoading,
  selectLogPagination,
  selectLogSummary,
} from '../../Modules/Log/Presentation/selectors';
import {
  fetchLogsForExport,
  fetchLogsPage,
  fetchLogsSummary,
  deleteLog,
  updateLogWithoutRecord,
} from '../../Modules/Log/Presentation/actions';
import { actionMeta, allowedActionKeys } from './components/helpers';
import LogTable from './components/LogTable';
import LogEditModal from './components/LogEditModal';
import { selectAccounts } from '../../Modules/Account/Presentation/selectors';
import { selectUser } from '../../Modules/User/Presentation/selectors';
import { canDeleteLogs } from '../../Utils/userPermissions';
import { getAccountIdById } from '../../Utils/getAccountNameById';
import { fetchAccounts } from '../../Modules/Account/Presentation/actions';
import { exportLogsToExcel } from '../../Utils/exportLogsToExcel';
import { LogParams } from '../../Modules/Log/Domain/Repositories/LogRepository';

const { Title } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

type SearchField = 'all' | 'description' | 'slot' | 'seller' | 'parent' | 'worker';
type AccountFilterType = 'sellerId' | 'sellerParentId' | 'actorId' | 'creatorId';
type ActiveFilter = 'today' | 'yesterday' | 'this_month' | 'custom';

const placeholders: Record<SearchField, string> = {
  all: '로그 ID · 셀러/상위계정 · 작업자 · 행위',
  description: '설명 검색',
  slot: '슬롯 번호 입력',
  seller: '셀러 ID 또는 아이디(username)',
  parent: '상위 계정 ID 또는 아이디(username)',
  worker: '작업자 username 검색',
};

const accountFilterLabels: Record<AccountFilterType, string> = {
  sellerId: '셀러',
  sellerParentId: '상위 계정',
  actorId: '행위자',
  creatorId: '생성자',
};

const LogsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const logs = useAppSelector(selectLogs);
  const loading = useAppSelector(selectLogLoading);
  const pagination = useAppSelector(selectLogPagination);
  const summary = useAppSelector(selectLogSummary);
  const accounts = useAppSelector(selectAccounts);
  const currentUser = useAppSelector(selectUser);
  const isAdmin = currentUser?.grade === 'ADMIN';
  const canDeleteLog = canDeleteLogs(currentUser);

  const [range, setRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [appliedRange, setAppliedRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [logStartDate, setLogStartDate] = useState<Dayjs | null>(null);
  const [appliedLogStartDate, setAppliedLogStartDate] = useState<string | null>(null);
  const [logEndDate, setLogEndDate] = useState<Dayjs | null>(null);
  const [appliedLogEndDate, setAppliedLogEndDate] = useState<string | null>(null);
  const [selectedActions, setSelectedActions] = useState<string[]>([]);
  const [appliedSelectedActions, setAppliedSelectedActions] = useState<string[]>([]);
  const [searchField, setSearchField] = useState<SearchField>('all');
  const [appliedSearchField, setAppliedSearchField] = useState<SearchField>('all');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [searchInputKey, setSearchInputKey] = useState(0);
  const searchDraftRef = useRef('');
  const [selectedAccountId, setSelectedAccountId] = useState<number | null>(null);
  const [appliedSelectedAccountId, setAppliedSelectedAccountId] = useState<number | null>(null);
  const [accountFilterType, setAccountFilterType] = useState<AccountFilterType>('sellerId');
  const [appliedAccountFilterType, setAppliedAccountFilterType] =
    useState<AccountFilterType>('sellerId');
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>('today');
  const [appliedActiveFilter, setAppliedActiveFilter] = useState<ActiveFilter>('today');
  const [selected, setSelected] = useState<React.Key[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [excelDownloading, setExcelDownloading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingLog, setEditingLog] = useState<any>(null);

  useEffect(() => {
    if (accounts.length === 0) dispatch(fetchAccounts());
  }, [dispatch, accounts.length]);

  const actionOptions = useMemo(() => {
    return Array.from(allowedActionKeys)
      .filter(k => !!actionMeta[k])
      .sort((a, b) => (actionMeta[a].label).localeCompare(actionMeta[b].label));
  }, []);

  const accountOptions = useMemo(() => {
    const targetAccounts = accountFilterType === 'sellerId'
      ? (accounts ?? []).filter((acc: any) => acc.grade === 'SELLER')
      : (accounts ?? []);

    return targetAccounts
      .slice()
      .sort((a: any, b: any) => String(a.username ?? '').localeCompare(String(b.username ?? '')))
      .map((acc: any) => ({
        value: acc.id as number,
        label: getAccountIdById(accounts as any, acc.id as number, true, currentUser as any) ?? String(acc.username ?? acc.id),
      }));
  }, [accounts, currentUser, accountFilterType]);

  const resetSelectionAndFirstPage = useCallback(() => {
    setSelected([]);
    setPage(1);
  }, []);

  const buildBaseParams = useCallback((): LogParams => {
    const params: LogParams = {};
    const hasLogSlotDateFilter = Boolean(appliedLogStartDate || appliedLogEndDate);
    const hasCreatedAtRange = appliedActiveFilter === 'custom' && appliedRange;

    if (hasCreatedAtRange) {
      const startDate = appliedRange[0]?.format('YYYY-MM-DD');
      const endDate = appliedRange[1]?.format('YYYY-MM-DD');

      if (startDate && endDate && startDate === endDate) {
        params.date = startDate;
      } else {
        if (startDate) params.start_date = startDate;
        if (endDate) params.end_date = endDate;
      }
    } else if (!hasLogSlotDateFilter && appliedActiveFilter !== 'custom') {
      params.filter_type = appliedActiveFilter;
    }

    if (appliedLogStartDate) {
      params.log_start_date = appliedLogStartDate;
    }

    if (appliedLogEndDate) {
      params.log_end_date = appliedLogEndDate;
    }

    if (appliedSelectedAccountId != null) {
      if (appliedAccountFilterType === 'sellerId') params.sellerId = appliedSelectedAccountId;
      if (appliedAccountFilterType === 'sellerParentId') {
        params.sellerParentId = appliedSelectedAccountId;
      }
      if (appliedAccountFilterType === 'actorId') params.actorId = appliedSelectedAccountId;
      if (appliedAccountFilterType === 'creatorId') {
        params.creatorId = appliedSelectedAccountId;
      }
    }

    if (appliedSelectedActions.length > 0) {
      params.action = appliedSelectedActions.join(',');
    }

    const trimmedSearch = appliedSearch.trim();
    if (trimmedSearch) {
      switch (appliedSearchField) {
        case 'all':
          params.all = trimmedSearch;
          break;
        case 'description':
          params.keyword = trimmedSearch;
          break;
        case 'slot': {
          const slotId = Number(trimmedSearch);
          if (Number.isInteger(slotId) && slotId > 0) {
            params.slotId = slotId;
          }
          break;
        }
        case 'seller':
          params.actor = trimmedSearch;
          break;
        case 'parent':
          params.parent = trimmedSearch;
          break;
        case 'worker':
          params.worker = trimmedSearch;
          break;
      }
    }

    return params;
  }, [
    appliedActiveFilter,
    appliedRange,
    appliedSelectedAccountId,
    appliedAccountFilterType,
    appliedSelectedActions,
    appliedSearch,
    appliedSearchField,
    appliedLogStartDate,
    appliedLogEndDate,
  ]);

  const buildServerParams = useCallback((includePagination = true): LogParams => {
    const params = buildBaseParams();

    if (includePagination) {
      params.page = page;
      params.page_size = pageSize;
    }

    return params;
  }, [buildBaseParams, page, pageSize]);

  const refreshLogs = useCallback(() => {
    return dispatch(fetchLogsPage(buildServerParams(true)));
  }, [dispatch, buildServerParams]);

  const refreshSummary = useCallback(() => {
    return dispatch(fetchLogsSummary(buildBaseParams()));
  }, [dispatch, buildBaseParams]);

  useEffect(() => {
    refreshLogs()
      .unwrap()
      .catch(() => {
        message.error('로그 조회에 실패했습니다.');
      });
  }, [refreshLogs]);

  useEffect(() => {
    refreshSummary()
      .unwrap()
      .catch(() => undefined);
  }, [refreshSummary]);

  const handleRange = useCallback((v: null | [Dayjs | null, Dayjs | null]) => {
    if (!v || (!v[0] && !v[1])) {
      setRange(null);
      return;
    }

    setRange([v[0], v[1]]);
    setActiveFilter('custom');
  }, []);

  const totalDaysSummary = useMemo(() => {
    if (summary) {
      return {
        plus: Number(summary.plus_days ?? 0),
        minus: -Math.abs(Number(summary.minus_days ?? 0)),
        total: Number(summary.total_days ?? 0),
      };
    }

    let plus = 0;
    let minus = 0;
    let total = 0;

    for (const l of logs as any[]) {
      const v = Number(l?.total_days ?? 0);
      if (!Number.isFinite(v)) continue;
      if (v > 0) plus += v;
      else if (v < 0) minus += v;
      total += v;
    }

    return { plus, minus, total };
  }, [logs, summary]);

  const totalCount = summary?.count ?? pagination?.count ?? logs.length;

  const handleSelectCustomFilter = useCallback(() => {
    setActiveFilter('custom');
  }, []);

  const handleAccountFilterTypeChange = useCallback((value: AccountFilterType) => {
    setAccountFilterType(value);
    setSelectedAccountId(null);
  }, []);

  const handleAccountChange = useCallback((value: number | undefined) => {
    setSelectedAccountId(value ?? null);
  }, []);

  const handleSearchFieldChange = useCallback((value: SearchField) => {
    setSearchField(value);
    searchDraftRef.current = '';
    setSearchInputKey(prev => prev + 1);
  }, []);

  type QuickPeriod = 'today' | 'yesterday' | 'this_month';

  /**
   * draft → applied 반영 후 목록/요약 재조회
   * - `quickPeriod`: 오늘/어제/이번달 버튼 — 즉시 조회
   * - 생략: 검색 버튼·Enter — 기간(커스텀 포함)·나머지 필터는 현재 draft 기준
   */
  const applyFilters = useCallback(
    (quickPeriod?: QuickPeriod) => {
      if (quickPeriod) {
        setRange(null);
        setActiveFilter(quickPeriod);
        setLogStartDate(null);
        setLogEndDate(null);
      }

      const targetFilter: ActiveFilter = quickPeriod ?? activeFilter;
      const hasLogSlotDateDraft = Boolean(logStartDate || logEndDate);

      if (targetFilter === 'custom' && !hasLogSlotDateDraft) {
        if (!range?.[0] || !range?.[1]) {
          message.warning('날짜 범위를 선택해주세요.');
          return;
        }
      }

      const trimmed = searchDraftRef.current.trim();
      if (searchField === 'slot' && trimmed) {
        const slotId = Number(trimmed);
        if (!Number.isInteger(slotId) || slotId <= 0) {
          message.warning('슬롯 번호는 숫자로 입력해주세요.');
          return;
        }
      }

      const nextAppliedLogStartDate = quickPeriod
        ? null
        : logStartDate
          ? logStartDate.format('YYYY-MM-DD')
          : null;
      const nextAppliedLogEndDate = quickPeriod
        ? null
        : logEndDate
          ? logEndDate.format('YYYY-MM-DD')
          : null;

      setAppliedActiveFilter(targetFilter);
      if (targetFilter === 'custom' && range?.[0] && range?.[1]) {
        setAppliedRange([range[0], range[1]]);
      } else {
        setAppliedRange(null);
      }
      setAppliedAccountFilterType(accountFilterType);
      setAppliedSelectedAccountId(selectedAccountId);
      setAppliedSelectedActions([...selectedActions]);
      setAppliedSearchField(searchField);
      setAppliedSearch(trimmed);
      setAppliedLogStartDate(nextAppliedLogStartDate);
      setAppliedLogEndDate(nextAppliedLogEndDate);
      resetSelectionAndFirstPage();
    },
    [
      activeFilter,
      range,
      searchField,
      accountFilterType,
      selectedAccountId,
      selectedActions,
      logStartDate,
      logEndDate,
      resetSelectionAndFirstPage,
    ],
  );

  const handleTodayLogs = useCallback(() => {
    applyFilters('today');
  }, [applyFilters]);

  const handleYesterdayLogs = useCallback(() => {
    applyFilters('yesterday');
  }, [applyFilters]);

  const handleThisMonthLogs = useCallback(() => {
    applyFilters('this_month');
  }, [applyFilters]);

  const handleApplySearch = useCallback(() => {
    applyFilters();
  }, [applyFilters]);

  const handleActionChange = useCallback((values: string[]) => {
    setSelectedActions(values);
  }, []);

  const handlePageChange = useCallback((nextPage: number, nextPageSize?: number) => {
    if (nextPageSize && nextPageSize !== pageSize) {
      setSelected([]);
      setPageSize(nextPageSize);
    }
    setPage(nextPage);
  }, [pageSize]);

  const handleDownloadLogs = useCallback(async () => {
    setExcelDownloading(true);

    try {
      const serverLogs = await dispatch(fetchLogsForExport(buildServerParams(false))).unwrap();
      const logsToExport = selected.length > 0
        ? serverLogs.filter(log => selected.includes(log.logId))
        : serverLogs;

      if (logsToExport.length === 0) {
        message.warning('다운로드할 로그가 없습니다.');
        return;
      }

      exportLogsToExcel(
        logsToExport as any,
        accounts as any,
        appliedActiveFilter,
        appliedRange ? [appliedRange[0], appliedRange[1]] : null,
      );
      message.success(
        selected.length > 0
          ? `${logsToExport.length}개 선택 로그의 엑셀 다운로드가 시작되었습니다.`
          : `${logsToExport.length}개 로그를 서버에서 다시 조회해 엑셀 다운로드를 시작했습니다.`,
      );
    } catch {
      message.error('엑셀 다운로드에 실패했습니다.');
    } finally {
      setExcelDownloading(false);
    }
  }, [dispatch, buildServerParams, selected, accounts, appliedActiveFilter, appliedRange]);

  const handleDeleteLog = useCallback((logId: number) => {
    if (!currentUser || !canDeleteLogs(currentUser)) {
      message.warning('제한형 관리자는 로그를 삭제할 수 없습니다.');
      return;
    }
    dispatch(deleteLog({ logId, me: currentUser }))
      .unwrap()
      .then(async () => {
        setSelected(prev => prev.filter(id => id !== logId));
        message.success('로그가 삭제되었습니다.');
        await Promise.all([refreshLogs().unwrap(), refreshSummary().unwrap()]);
      })
      .catch(() => {
        message.error('로그 삭제에 실패했습니다.');
      });
  }, [currentUser, dispatch, refreshLogs, refreshSummary]);

  const handleBatchDelete = useCallback(() => {
    if (!currentUser || !canDeleteLogs(currentUser)) {
      message.warning('제한형 관리자는 로그를 삭제할 수 없습니다.');
      return;
    }
    if (selected.length === 0) {
      message.warning('삭제할 로그를 선택해주세요.');
      return;
    }

    Promise.all(
      selected.map(logId =>
        dispatch(deleteLog({ logId: logId as number, me: currentUser })).unwrap(),
      ),
    )
      .then(async () => {
        message.success(`${selected.length}개 로그가 삭제되었습니다.`);
        setSelected([]);
        await Promise.all([refreshLogs().unwrap(), refreshSummary().unwrap()]);
      })
      .catch(() => {
        message.error('로그 삭제에 실패했습니다.');
      });
  }, [currentUser, selected, dispatch, refreshLogs, refreshSummary]);

  const handleEdit = useCallback((log: any) => {
    setEditingLog(log);
    setEditOpen(true);
  }, []);

  const handleSubmitEdit = useCallback(async (payload: any) => {
    if (!editingLog) return;
    try {
      await dispatch(updateLogWithoutRecord({ logId: editingLog.logId, payload })).unwrap();
      message.success('로그가 수정되었습니다.');
      setEditOpen(false);
      setEditingLog(null);
      await Promise.all([refreshLogs().unwrap(), refreshSummary().unwrap()]);
    } catch {
      message.error('로그 수정에 실패했습니다.');
    }
  }, [dispatch, editingLog, refreshLogs, refreshSummary]);

  return (
    <>
      <Title level={3}>활동 로그</Title>

      <div
        style={{
          display: 'flex',
          gap: 8,
          marginBottom: 16,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <Button
          type={activeFilter === 'today' ? 'primary' : 'default'}
          onClick={handleTodayLogs}
        >
          오늘
        </Button>
        <Button
          type={activeFilter === 'yesterday' ? 'primary' : 'default'}
          onClick={handleYesterdayLogs}
        >
          어제
        </Button>
        <Button
          type={activeFilter === 'this_month' ? 'primary' : 'default'}
          onClick={handleThisMonthLogs}
        >
          이번달
        </Button>
        <Button
          type={activeFilter === 'custom' ? 'primary' : 'default'}
          onClick={handleSelectCustomFilter}
        >
          기간 조회
        </Button>

        <Tooltip title="현재 조회/필터링된 결과 전체를 기준으로 합계를 계산합니다.">
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '6px 10px',
              border: '1px solid #f0f0f0',
              borderRadius: 8,
              background: '#fafafa',
            }}
          >
            <Tag color="green">+{totalDaysSummary.plus}일</Tag>
            <Tag color="red">{totalDaysSummary.minus}일</Tag>
            <Tag color="blue">총 {totalDaysSummary.total}일</Tag>
          </div>
        </Tooltip>

        <Button type="primary" onClick={handleDownloadLogs} loading={excelDownloading}>
          엑셀 다운로드{((selected.length > 0 ? selected.length : totalCount) > 0) && ` (${selected.length > 0 ? selected.length : totalCount}개)`}
        </Button>

        {canDeleteLog && selected.length > 0 && (
          <Popconfirm
            title={`${selected.length}개 로그를 삭제하시겠습니까?`}
            description="삭제된 로그는 복구할 수 없습니다."
            onConfirm={handleBatchDelete}
            okText="삭제"
            cancelText="취소"
            okType="danger"
          >
            <Button danger icon={<DeleteOutlined />}>
              선택 삭제 ({selected.length}개)
            </Button>
          </Popconfirm>
        )}
      </div>

      <div
        style={{
          display: 'flex',
          gap: 12,
          marginBottom: 16,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <Space size={12} wrap style={{ flex: 1, minWidth: 320 }}>
          <RangePicker
            value={range}
            onChange={handleRange}
            allowEmpty={[true, true]}
            placeholder={['로그 생성 시작', '로그 생성 종료']}
          />

          <DatePicker
            value={logStartDate}
            onChange={setLogStartDate}
            allowClear
            placeholder="슬롯 시작일"
          />

          <DatePicker
            value={logEndDate}
            onChange={setLogEndDate}
            allowClear
            placeholder="슬롯 종료일"
          />

          <Space size={8} wrap>
            <Select
              value={accountFilterType}
              onChange={handleAccountFilterTypeChange}
              style={{ width: 140 }}
            >
              <Option value="sellerId">셀러</Option>
              <Option value="sellerParentId">상위 계정</Option>
              <Option value="actorId">행위자</Option>
              <Option value="creatorId">생성자</Option>
            </Select>
            <Select
              showSearch
              allowClear
              placeholder={`${accountFilterLabels[accountFilterType]} 선택`}
              value={selectedAccountId ?? undefined}
              onChange={handleAccountChange}
              style={{ width: 240 }}
              optionFilterProp="children"
              filterOption={(input, option) =>
                String(option?.children ?? '').toLowerCase().includes(input.toLowerCase())
              }
            >
              {accountOptions.map(o => (
                <Option key={o.value} value={o.value}>
                  {o.label}
                </Option>
              ))}
            </Select>
          </Space>

          <Select
            mode="multiple"
            allowClear
            placeholder="행위 선택 (다중)"
            value={selectedActions}
            onChange={handleActionChange}
            style={{ minWidth: 260 }}
            maxTagCount="responsive"
            optionLabelProp="label"
            tagRender={(props) => {
              const { label, value, closable, onClose } = props as any;
              const meta = actionMeta[value as string];
              return (
                <Tag color={meta?.color} closable={closable} onClose={onClose} style={{ marginRight: 3 }}>
                  {label}
                </Tag>
              );
            }}
          >
            {actionOptions.map(a => {
              const meta = actionMeta[a];
              return (
                <Option key={a} value={a} label={meta.label}>
                  <span>
                    <Tag color={meta.color} style={{ marginRight: 8 }}>{meta.label}</Tag>
                  </span>
                </Option>
              );
            })}
          </Select>

          <Space size={8} wrap>
            <Select
              placeholder="검색 범주"
              value={searchField}
              onChange={handleSearchFieldChange}
              style={{ width: 140 }}
            >
              <Option value="all">통합</Option>
              <Option value="description">설명</Option>
              <Option value="slot">슬롯 번호</Option>
              <Option value="seller">셀러</Option>
              <Option value="parent">상위 계정</Option>
              {isAdmin && <Option value="worker">작업자</Option>}
            </Select>
            <Input
              key={searchInputKey}
              defaultValue=""
              placeholder={placeholders[searchField]}
              allowClear
              style={{ width: 260 }}
              onChange={(e) => {
                searchDraftRef.current = e.target.value;
              }}
              onPressEnter={handleApplySearch}
            />
            <Button type="primary" onClick={handleApplySearch}>
              검색
            </Button>
          </Space>
        </Space>
      </div>

      <LogTable
        data={logs}
        loading={loading}
        selected={selected}
        onSelectChange={setSelected}
        onDelete={handleDeleteLog}
        onEdit={handleEdit}
        pagination={pagination}
        onPageChange={handlePageChange}
      />

      <LogEditModal
        open={editOpen}
        log={editingLog}
        onClose={() => { setEditOpen(false); setEditingLog(null); }}
        onSubmit={handleSubmitEdit}
      />
    </>
  );
};

export default LogsPage;
